/**
 * Nova custom variant picker (vs base Horizon). Loaded as @theme/variant-picker.
 * Custom dropdown UI, color/size persistence, legend restore; see NOVATIZE CUSTOM block in variant-main-picker.liquid.
 */
import {Component} from "@theme/component";
import {VariantSelectedEvent, VariantUpdateEvent} from "@theme/events";
import {morph, MORPH_OPTIONS} from "@theme/morph";
import {yieldToMainThread, getViewParameterValue, ResizeNotifier} from "@theme/utilities";

/**
 * Selectors and class names for variant picker DOM. Single source of truth; must match variant-main-picker.liquid.
 * @constant
 */
const VariantPickerDOM = Object.freeze({
  customDropdown: "[data-custom-dropdown]",
  customDropdownOpen: '[data-custom-dropdown][data-dropdown-open="true"]',
  customDropdownTrigger: ".variant-option__custom-dropdown-trigger",
  customDropdownList: ".variant-option__custom-dropdown-list",
  customDropdownLabel: ".variant-option__custom-dropdown-label",
  customDropdownOption: ".variant-option__custom-dropdown-option",
  customDropdownOptionPlaceholder: "variant-option__custom-dropdown-option--placeholder",
  customDropdownOptionUnavailable: "variant-option__custom-dropdown-option--unavailable",
  select: "select.variant-option__select",
  colorSwatchButton: "button.variant-option__button-label--has-swatch",
  colorSwatchButtonSelected: "button.variant-option__button-label--has-swatch.variant-option__button-label--selected",
  colorSwatchClass: "variant-option__button-label--has-swatch",
  colorSwatchSelectedClass: "variant-option__button-label--selected",
  legendColorGroupLabel: "legend[data-color-group-label]",
  legendSelectedColorInfo: "[data-selected-color-info]",
  legendSelectedColorName: "[data-selected-color-name]",
  selectedOptions: "select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]",
  fieldsetVariantNotColorGroup: "fieldset.variant-option:not([data-color-group])",
});

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach(fieldset => {
      const radios = Array.from(fieldset?.querySelectorAll("input") ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(radio => radio.dataset.currentChecked === "true");
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener("change", this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.#boundCustomDropdownClick = this.#onCustomDropdownClick.bind(this);
    this.addEventListener("click", this.#boundCustomDropdownClick);
    this.#initCustomDropdowns();
    this.#updateAtcButtonState();
    this.#boundCloseCustomDropdownsOnOutsideClick = this.#closeCustomDropdownsOnOutsideClick.bind(this);
    document.addEventListener("click", this.#boundCloseCustomDropdownsOnOutsideClick);
    this.#boundColorSwatchClick = this.#onColorSwatchClick.bind(this);
    this.addEventListener("click", this.#boundColorSwatchClick);
    this.#boundRadioSwatchChange = this.#onRadioSwatchChange.bind(this);
    this.addEventListener("change", this.#boundRadioSwatchChange);
    setTimeout(() => this.#updateInitialLegend(), 100);
  }

  #sizeExplicitlySelected = false;

  /** @type {((e: MouseEvent) => void) | undefined} */
  #boundCustomDropdownClick;

  /** @type {((e: MouseEvent) => void) | undefined} */
  #boundCloseCustomDropdownsOnOutsideClick;

  /** @type {((e: MouseEvent) => void) | undefined} */
  #boundColorSwatchClick;

  /** @type {((e: Event) => void) | undefined} */
  #boundRadioSwatchChange;

  disconnectedCallback() {
    this.removeEventListener("click", this.#boundCustomDropdownClick);
    document.removeEventListener("click", this.#boundCloseCustomDropdownsOnOutsideClick);
    this.removeEventListener("click", this.#boundColorSwatchClick);
    this.removeEventListener("change", this.#boundRadioSwatchChange);
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Delegated click handler for custom dropdown trigger and options. Works for current and morphed-in DOM.
   * @param {MouseEvent} e
   */
  #onCustomDropdownClick(e) {
    const target =
      e.target instanceof HTMLElement
        ? e.target.closest(`${VariantPickerDOM.customDropdownTrigger}, ${VariantPickerDOM.customDropdownOption}`)
        : null;
    if (!target) return;

    const wrapper = target.closest(VariantPickerDOM.customDropdown);
    if (!wrapper) return;

    const select = wrapper.querySelector(VariantPickerDOM.select);
    const trigger = wrapper.querySelector(VariantPickerDOM.customDropdownTrigger);
    const list = wrapper.querySelector(VariantPickerDOM.customDropdownList);
    if (!select || !trigger || !list) return;

    if (target.classList.contains(VariantPickerDOM.customDropdownTrigger.slice(1))) {
      e.stopPropagation();
      const isOpen = wrapper.getAttribute("data-dropdown-open") === "true";
      if (isOpen) {
        this.#closeCustomDropdown(wrapper);
      } else {
        wrapper.setAttribute("data-dropdown-open", "true");
        trigger.setAttribute("aria-expanded", "true");
        list.hidden = false;
      }
      return;
    }

    if (target.classList.contains(VariantPickerDOM.customDropdownOption.slice(1))) {
      e.stopPropagation();
      if (target.classList.contains(VariantPickerDOM.customDropdownOptionPlaceholder)) {
        return;
      }
      if (wrapper.dataset.sizeOption) {
        this.#sizeExplicitlySelected = true;
        this.#updateAtcButtonState();
      }
      const optionValueId = target.getAttribute("data-option-value-id");
      const value = target.getAttribute("data-value");
      if (value === null && !optionValueId) return;
      const nativeOption = optionValueId
        ? Array.from(select.options).find(opt => opt.dataset.optionValueId === optionValueId)
        : Array.from(select.options).find(opt => opt.value === value);
      if (nativeOption) {
        select.value = nativeOption.value;
      } else if (value !== null) {
        select.value = value;
      }
      this.#syncCustomDropdownTriggerLabel(select, trigger);
      this.#closeCustomDropdown(wrapper);
      select.dispatchEvent(new Event("change", {bubbles: true}));
    }
  }

  /**
   * Closes any open custom dropdown when the click is outside the dropdown wrapper.
   * @param {MouseEvent} e
   */
  #closeCustomDropdownsOnOutsideClick(e) {
    const openWrappers = this.querySelectorAll(VariantPickerDOM.customDropdownOpen);
    openWrappers.forEach(wrapper => {
      if (!wrapper.contains(/** @type {Node} */ (e.target))) {
        this.#closeCustomDropdown(wrapper);
      }
    });
  }

  /**
   * @param {HTMLElement} wrapper
   */
  #closeCustomDropdown(wrapper) {
    wrapper.removeAttribute("data-dropdown-open");
    const trigger = wrapper.querySelector(VariantPickerDOM.customDropdownTrigger);
    const list = wrapper.querySelector(VariantPickerDOM.customDropdownList);
    if (trigger) trigger.setAttribute("aria-expanded", "false");
    if (list) list.hidden = true;
  }

  /**
   * Syncs the custom dropdown trigger label from the native select's selected option text.
   * @param {HTMLSelectElement} select
   * @param {HTMLElement} trigger
   */
  #syncCustomDropdownTriggerLabel(select, trigger) {
    const labelEl = trigger.querySelector(VariantPickerDOM.customDropdownLabel);
    if (!labelEl) return;
    const option = select.options[select.selectedIndex];
    labelEl.textContent = option ? option.textContent : labelEl.textContent;
  }

  /**
   * Syncs custom dropdown trigger labels from the native select. Call after morph so updated dropdowns show the right value.
   * Click behaviour is handled by delegated #onCustomDropdownClick on the variant-picker, so no per-element binding needed.
   */
  #initCustomDropdowns() {
    const wrappers = this.querySelectorAll(VariantPickerDOM.customDropdown);
    wrappers.forEach(wrapper => {
      if (wrapper.dataset.sizeOption && !this.#sizeExplicitlySelected) return;
      const select = wrapper.querySelector(VariantPickerDOM.select);
      const trigger = wrapper.querySelector(VariantPickerDOM.customDropdownTrigger);
      if (!select || !trigger) return;
      this.#syncCustomDropdownTriggerLabel(select, trigger);
    });
  }

  #getAtcButtons() {
    const sectionId = this.dataset.sectionId;
    const scope = sectionId
      ? document.getElementById(`shopify-section-${sectionId}`)
      : this.closest("quick-add-dialog, featured-product-information");
    if (!scope) return [];
    return Array.from(scope.querySelectorAll('button[name="add"], .sticky-add-to-cart__button'));
  }

  #updateAtcButtonState() {
    if (!this.querySelector("[data-size-option]")) return;
    if (this.#sizeExplicitlySelected) return;
    this.#getAtcButtons().forEach(btn => {
      btn.setAttribute("disabled", "");
      const defaultText = btn.dataset.defaultAddToCartText;
      const textContainer = btn.querySelector(".add-to-cart-text__content");
      const textEl = textContainer
        ? Array.from(textContainer.children).find(el => !el.classList.contains("quantity-display"))
        : null;
      if (defaultText && textEl) textEl.textContent = defaultText;
    });
  }

  /**
   * NOVATIZE: Click handler for color swatch buttons – store selection, update legends, dispatch change.
   * @param {MouseEvent} e
   */
  #onColorSwatchClick(e) {
    const button = e.target instanceof HTMLElement ? e.target.closest("button.variant-option__button-label") : null;
    if (!button) return;
    if (!button.dataset.optionValueId) return;
    if (!button.classList.contains(VariantPickerDOM.colorSwatchClass)) return;

    const isSeasonalGroup = !!button.closest("fieldset[data-seasonal-group]");

    const uniqueId = (button.dataset.linkedProductId || "") + "-" + (button.dataset.optionValueId || "");
    button.dataset.uniqueColorId = uniqueId;

    this.dataset.selectedUniqueColorId = uniqueId;
    this.dataset.selectedOptionValueId = button.dataset.optionValueId || "";
    this.dataset.selectedLinkedProductId = button.dataset.linkedProductId || "";
    const colorName = button.getAttribute("aria-label");
    if (colorName) this.dataset.selectedColorName = colorName;

    const allColorButtons = this.querySelectorAll(VariantPickerDOM.colorSwatchButton);
    allColorButtons.forEach(btn => {
      if (btn !== button) {
        btn.classList.remove(VariantPickerDOM.colorSwatchSelectedClass);
        btn.setAttribute("aria-pressed", "false");
        btn.dataset.currentChecked = "false";
        btn.dataset.previousChecked = "false";
      }
    });

    this.querySelectorAll(VariantPickerDOM.legendColorGroupLabel).forEach(legend => {
      const selectedColorInfo = legend.querySelector(VariantPickerDOM.legendSelectedColorInfo);
      if (selectedColorInfo) selectedColorInfo.textContent = legend.dataset.productCode || "";
    });

    button.classList.add(VariantPickerDOM.colorSwatchSelectedClass);
    button.setAttribute("aria-pressed", "true");
    button.dataset.currentChecked = "true";

    // JR-31975: group/legend label always stays static -- only this "currently selected color
    // name" element (rendered below the legend) reflects the clicked swatch.
    const fieldset = button.closest("fieldset.variant-option--swatches");
    if (fieldset) {
      const selectedColorNameEl = fieldset.querySelector(VariantPickerDOM.legendSelectedColorName);
      if (selectedColorNameEl) {
        selectedColorNameEl.textContent = button.dataset.colorName || button.getAttribute("aria-label") || "";
      }
    }

    if (!isSeasonalGroup) {
      const colorGroup = button.closest("fieldset[data-color-group]");
      if (colorGroup) {
        const legend = colorGroup.querySelector("legend");
        if (legend) {
          const selectedColorInfo = legend.querySelector(VariantPickerDOM.legendSelectedColorInfo);
          if (selectedColorInfo) selectedColorInfo.textContent = legend.dataset.productCode || button.dataset.colorHex || "";
        }
      }
    }

    // JR-32046: switching color should prompt the customer to re-select a size instead of
    // silently keeping (or Shopify auto-picking) a size that may not exist/be in stock for the
    // newly selected color.
    this.#sizeExplicitlySelected = false;
    this.#resetSizeDropdowns();
    this.#updateAtcButtonState();

    const changeEvent = new Event("change", {bubbles: true, cancelable: true});
    Object.defineProperty(changeEvent, "target", {
      value: button,
      writable: false,
      enumerable: true,
      configurable: false,
    });
    button.dispatchEvent(changeEvent);
  }

  /**
   * NOVATIZE (JR-32046): Actively clears any already-selected size back to its placeholder
   * label/value. Unlike #initCustomDropdowns' skip-sync (which only prevents a not-yet-displayed
   * value from being synced in on cold load), this reverts a size the customer already picked --
   * needed when a color change should reset the size choice.
   */
  #resetSizeDropdowns() {
    this.querySelectorAll(VariantPickerDOM.customDropdown).forEach(wrapper => {
      if (!wrapper.dataset.sizeOption) return;
      const select = wrapper.querySelector(VariantPickerDOM.select);
      const trigger = wrapper.querySelector(VariantPickerDOM.customDropdownTrigger);
      const labelEl = trigger?.querySelector(VariantPickerDOM.customDropdownLabel);
      const placeholderOption = select?.querySelector('option[value=""]');

      if (select) {
        // Deliberately leave no option with the "selected" HTML attribute (not even the
        // placeholder, which has no data-option-value-id) -- selectedOptionsValues/
        // selectedOptionId read option[selected] and throw on a match with no option value ID.
        Array.from(select.options).forEach(opt => {
          opt.selected = false;
          opt.removeAttribute("selected");
        });
        select.value = "";
      }

      if (labelEl && placeholderOption) {
        labelEl.textContent = placeholderOption.textContent.trim();
      }
    });
  }

  /**
   * NOVATIZE: Set legend selected color info (HEX) from swatch element.
   * @param {HTMLElement} legend
   * @param {HTMLElement} swatchEl
   */
  #setSelectedColorInfo(legend, swatchEl) {
    const infoEl = legend.querySelector(VariantPickerDOM.legendSelectedColorInfo);
    if (infoEl) infoEl.textContent = legend.dataset.productCode || swatchEl.dataset.colorHex || "";
  }

  /**
   * NOVATIZE: On change, update legend for radio-based swatch fieldsets (non–color-group).
   * @param {Event} e
   */
  #onRadioSwatchChange(e) {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== "radio" || !input.matches('input[name*="-"]')) return;
    const fieldset = input.closest("fieldset.variant-option--swatches");
    if (!fieldset || fieldset.dataset.colorGroup) return;
    const legend = fieldset.querySelector("legend");
    const label = input.closest("label" + VariantPickerDOM.colorSwatchClass);
    if (legend && label) this.#setSelectedColorInfo(legend, label);
  }

  /**
   * NOVATIZE: Update legend for initially selected color (run only once per picker; after morph, restore runs in fetchUpdatedSection).
   */
  #updateInitialLegend() {
    if (this.dataset.legendInitialUpdateDone === "1") return;
    this.dataset.legendInitialUpdateDone = "1";

    this.querySelectorAll(VariantPickerDOM.legendColorGroupLabel).forEach(legend => {
      const infoEl = legend.querySelector(VariantPickerDOM.legendSelectedColorInfo);
      const fieldset = legend.closest("fieldset[data-color-group]");
      if (!fieldset) return;

      const selectedButton = fieldset.querySelector(VariantPickerDOM.colorSwatchButtonSelected);
      if (infoEl) infoEl.textContent = legend.dataset.productCode || selectedButton?.dataset.colorHex || "";
    });

    this.querySelectorAll("fieldset.variant-option--swatches").forEach(fieldset => {
      if (fieldset.dataset.colorGroup) return;
      const legend = fieldset.querySelector("legend");
      const checkedInput = fieldset.querySelector("input[type=radio]:checked");
      const label = checkedInput && checkedInput.closest("label" + VariantPickerDOM.colorSwatchClass);
      if (legend && label) this.#setSelectedColorInfo(legend, label);
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    // For color swatch buttons, ensure we use the exact button that was clicked
    // This prevents issues when multiple buttons have the same color name
    const isColorSwatchButton =
      selectedOption instanceof HTMLButtonElement &&
      selectedOption.classList.contains(VariantPickerDOM.colorSwatchClass);

    if (isColorSwatchButton) {
      // Store the clicked button's option value ID to ensure we use the correct one
      this.dataset.lastClickedOptionValueId = selectedOption.dataset.optionValueId || "";
      this.dataset.lastClickedVariantId = selectedOption.dataset.variantId || "";
      this.dataset.lastClickedProductUrl = selectedOption.dataset.connectedProductUrl || "";
      // Persist selected color so size (and other option) changes keep this color in the request URL
      this.dataset.selectedOptionValueId = selectedOption.dataset.optionValueId || "";
      this.dataset.selectedLinkedProductId = selectedOption.dataset.connectedProductUrl || "";
      this.dataset.selectedColorName = selectedOption.getAttribute("aria-label") || "";
      this.dataset.selectedUniqueColorId = selectedOption.dataset.uniqueColorId || "";
      this.#sizeExplicitlySelected = false;
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({id: selectedOption.dataset.optionValueId ?? ""}));

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest("featured-product-information"));

    const isSeasonalCurrentProduct =
      isColorSwatchButton &&
      !!selectedOption.closest("fieldset[data-seasonal-group]") &&
      selectedOption.dataset.linkedProductId === this.dataset.productId;

    const morphElementSelector = loadsNewProduct
      ? "main"
      : isOnFeaturedProductSection
        ? "featured-product-information"
        : undefined;

    if (!isSeasonalCurrentProduct) {
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, isColorSwatchButton);
    }

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = window.novaFixLocalizedUrl ? window.novaFixLocalizedUrl(newUrl) : newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({fieldset, currentWidth, previousWidth, currentIndex, previousIndex}) {
    if (currentWidth) {
      fieldset.style.setProperty("--pill-width-current", `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty("--pill-width-current");
    }

    if (previousWidth) {
      fieldset.style.setProperty("--pill-width-previous", `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty("--pill-width-previous");
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      // When searching by option value ID, prefer buttons with unique color IDs to avoid conflicts
      // when multiple buttons have the same option value ID (from different products)
      let targetElement = this.querySelector(`button[data-option-value-id="${target}"][data-unique-color-id]`);
      if (!targetElement) {
        targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      }

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || "");
      const inputIndex = Number.parseInt(target.dataset.inputIndex || "");

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = "false";
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = "false";
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = "true";
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = "true";
            radios[newPreviousIndex].dataset.currentChecked = "false";
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    // Handle button elements (used for color swatches from linked products)
    if (target instanceof HTMLButtonElement && target.dataset.fieldsetIndex) {
      // For color swatches, we need to deselect ALL color buttons across ALL fieldsets
      // to ensure only one color is selected at a time, regardless of which group it's in
      const isColorSwatch = target.classList.contains(VariantPickerDOM.colorSwatchClass);

      if (isColorSwatch) {
        const uniqueId = target.dataset.uniqueColorId;

        const allColorButtons = this.querySelectorAll(VariantPickerDOM.colorSwatchButton);
        allColorButtons.forEach(btn => {
          if (btn !== target && (!uniqueId || btn.dataset.uniqueColorId !== uniqueId)) {
            btn.classList.remove(VariantPickerDOM.colorSwatchSelectedClass);
            btn.setAttribute("aria-pressed", "false");
            btn.dataset.currentChecked = "false";
            btn.dataset.previousChecked = "false";
          }
        });

        // Clear all checked indices for all fieldsets that contain color buttons
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        fieldsets.forEach((fieldset, idx) => {
          if (fieldset && fieldset.querySelector(VariantPickerDOM.colorSwatchButton)) {
            if (this.#checkedIndices[idx]) {
              this.#checkedIndices[idx] = [];
            }
          }
        });

        // For color swatches, directly update only the clicked button and return early
        // This prevents the fieldset-based logic from interfering
        // Make absolutely sure we're only updating the exact button that was clicked
        target.dataset.currentChecked = "true";
        target.setAttribute("aria-pressed", "true");
        target.classList.add(VariantPickerDOM.colorSwatchSelectedClass);
        return;
      }

      // For non-color buttons, use the normal fieldset-based logic
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || "");
      const inputIndex = Number.parseInt(target.dataset.inputIndex || "");

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex] || [];
        this.#checkedIndices[fieldsetIndex] = checkedIndices;

        // Get all buttons in this fieldset (not just radios)
        const allButtons = Array.from(fieldset?.querySelectorAll("button[data-input-index]") ?? []);

        if (allButtons.length > 0 && fieldset) {
          // Clear previous checked states in this fieldset
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && allButtons[currentIndex]) {
            allButtons[currentIndex].dataset.previousChecked = "false";
            allButtons[currentIndex].dataset.currentChecked = "false";
            allButtons[currentIndex].removeAttribute("aria-pressed");
          }
          if (previousIndex !== undefined && allButtons[previousIndex]) {
            allButtons[previousIndex].dataset.previousChecked = "false";
            allButtons[previousIndex].dataset.currentChecked = "false";
            allButtons[previousIndex].removeAttribute("aria-pressed");
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && allButtons[newCurrentIndex]) {
            allButtons[newCurrentIndex].dataset.currentChecked = "true";
            allButtons[newCurrentIndex].setAttribute("aria-pressed", "true");
            allButtons[newCurrentIndex].classList.add(VariantPickerDOM.colorSwatchSelectedClass);
          }

          if (newPreviousIndex !== undefined && allButtons[newPreviousIndex]) {
            allButtons[newPreviousIndex].dataset.previousChecked = "true";
            allButtons[newPreviousIndex].dataset.currentChecked = "false";
            allButtons[newPreviousIndex].removeAttribute("aria-pressed");
            allButtons[newPreviousIndex].classList.remove(VariantPickerDOM.colorSwatchSelectedClass);
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.dataset.currentChecked = "true";
      target.setAttribute("aria-pressed", "true");
      target.classList.add(VariantPickerDOM.colorSwatchSelectedClass);
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(option => option.value === newValue);

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    if (window.novaFixLocalizedUrl) productUrl = window.novaFixLocalizedUrl(productUrl);
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    // For color swatch buttons, use the last clicked option value ID to ensure we get the correct variant
    // This prevents issues when multiple buttons have the same color name but different option value IDs
    const isColorSwatchButton =
      selectedOption instanceof HTMLButtonElement &&
      selectedOption.classList.contains(VariantPickerDOM.colorSwatchClass);
    const lastClickedOptionValueId = this.dataset.lastClickedOptionValueId;

    if (this.selectedOptionsValues.length && !source) {
      if (isColorSwatchButton) {
        const clickedOptionValueId = selectedOption.dataset.optionValueId;
        if (clickedOptionValueId) {
          params.push(`option_values=${clickedOptionValueId}`);
        } else {
          params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
        }
      } else {
        // When a non-color option (like size) is selected, include current color so URL keeps color + new size
        const storedColorOptionValueId = this.dataset.selectedOptionValueId;
        const allSelectedOptions = Array.from(
          this.querySelectorAll(`${VariantPickerDOM.selectedOptions}, ${VariantPickerDOM.colorSwatchButtonSelected}`),
        );
        const colorOptionValueId =
          storedColorOptionValueId ||
          allSelectedOptions.find(
            opt => opt instanceof HTMLButtonElement && opt.classList.contains(VariantPickerDOM.colorSwatchClass),
          )?.dataset?.optionValueId;
        if (colorOptionValueId) {
          const optionValueIds = allSelectedOptions
            .map(opt => {
              if (opt instanceof HTMLButtonElement && opt.classList.contains(VariantPickerDOM.colorSwatchClass)) {
                return colorOptionValueId;
              }
              return opt.dataset.optionValueId;
            })
            .filter(id => id);
          // eslint-disable-next-line no-undef -- Set is a standard ES6 global
          const uniqueOptionValueIds = [...new Set([...optionValueIds, colorOptionValueId])];
          params.push(`option_values=${uniqueOptionValueIds.join(",")}`);
        } else {
          params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
        }
      }
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    } else if (isColorSwatchButton && lastClickedOptionValueId) {
      // If no selected options but we have a last clicked option value ID, use it
      params.push(`option_values=${lastClickedOptionValueId}`);
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      "quick-add-component": "section-rendering-product-card",
      "swatches-variant-picker-component": "section-rendering-product-card",
      "featured-product-information": this.closest("featured-product-information")?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find(sectionId => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join("&")}`;
    }

    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, isColorChange = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Before fetching, remember which color button was selected (if any)
    const selectedColorButton = this.querySelector(VariantPickerDOM.colorSwatchButtonSelected);
    const selectedUniqueColorId = selectedColorButton?.dataset.uniqueColorId;
    const selectedOptionValueId = selectedColorButton?.dataset.optionValueId;
    const selectedLinkedProductId = selectedColorButton?.dataset.linkedProductId;
    const selectedColorName = selectedColorButton?.getAttribute("aria-label") || this.dataset.selectedColorName;

    // Capture current non-color selections (size, etc.) from actual select value/checked state, not [selected] attribute.
    const selectedNonColorOptions = [];
    if (!isColorChange) {
      this.querySelectorAll(VariantPickerDOM.select).forEach(select => {
        const value = select.value;
        if (!value) return;
        const option = Array.from(select.options).find(opt => opt.value === value && opt.dataset.optionValueId);
        if (option) {
          selectedNonColorOptions.push({optionValueId: option.dataset.optionValueId, value});
        }
      });
      this.querySelectorAll(`${VariantPickerDOM.fieldsetVariantNotColorGroup} input[type="radio"]:checked`).forEach(
        input => {
          if (input.dataset.optionValueId) {
            selectedNonColorOptions.push({optionValueId: input.dataset.optionValueId, value: input.value});
          }
        },
      );
    }

    fetch(requestUrl, {signal: this.#abortController.signal})
      .then(response => response.text())
      .then(responseText => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === "main") {
          this.updateMain(html);
          this.#initCustomDropdowns();
          this.#updateAtcButtonState();
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
          this.#initCustomDropdowns();
          this.#updateAtcButtonState();
        } else {
          const newProduct = this.updateVariantPicker(html);

          // After morphing, stored data attributes may have been overwritten by the server HTML.
          // Use only the values we captured before the fetch so color selection is preserved when e.g. size changes.
          const targetUniqueColorId = selectedUniqueColorId;
          const targetOptionValueId = selectedOptionValueId;
          const targetLinkedProductId = selectedLinkedProductId;
          const targetColorName = selectedColorName;

          // Re-apply stored color to the variant picker so buildRequestUrl and future restores have it
          if (targetOptionValueId) this.dataset.selectedOptionValueId = targetOptionValueId;
          if (targetLinkedProductId) this.dataset.selectedLinkedProductId = targetLinkedProductId;
          if (targetColorName) this.dataset.selectedColorName = targetColorName;
          if (targetUniqueColorId) this.dataset.selectedUniqueColorId = targetUniqueColorId;

          // Restore non-color options (e.g. size) and sync custom dropdown labels – run after every morph
          const restoreNonColorOptions = () => {
            selectedNonColorOptions.forEach(({optionValueId, value}) => {
              if (!value) return;
              const el = this.querySelector(`[data-option-value-id="${optionValueId}"]`);
              if (el instanceof HTMLOptionElement) {
                const select = el.closest("select");
                if (select) {
                  Array.from(select.options).forEach(opt => {
                    opt.selected = false;
                    opt.removeAttribute("selected");
                  });
                  el.selected = true;
                  el.setAttribute("selected", "selected");
                  select.value = el.value;
                }
              } else if (el instanceof HTMLInputElement && el.type === "radio") {
                el.checked = true;
              } else {
                // Fallback when option value IDs differ (e.g. switched color group / different product): restore by value
                const selects = this.querySelectorAll(VariantPickerDOM.select);
                selects.forEach(select => {
                  const option = Array.from(select.options).find(opt => opt.value === value);
                  if (option) {
                    Array.from(select.options).forEach(opt => {
                      opt.selected = false;
                      opt.removeAttribute("selected");
                    });
                    option.selected = true;
                    option.setAttribute("selected", "selected");
                    select.value = value;
                  }
                });
                const fieldsets = this.querySelectorAll(VariantPickerDOM.fieldsetVariantNotColorGroup);
                fieldsets.forEach(fieldset => {
                  const input = fieldset.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
                  if (input instanceof HTMLInputElement) input.checked = true;
                });
              }
            });
          };

          if (targetOptionValueId || targetUniqueColorId) {
            // Try to find the button by unique color ID first, then by option value ID
            let restoredButton = null;
            if (targetUniqueColorId) {
              restoredButton = this.querySelector(
                `${VariantPickerDOM.colorSwatchButton}[data-unique-color-id="${targetUniqueColorId}"]`,
              );
            }
            if (!restoredButton && targetLinkedProductId && targetOptionValueId) {
              restoredButton = this.querySelector(
                `${VariantPickerDOM.colorSwatchButton}[data-linked-product-id="${targetLinkedProductId}"][data-option-value-id="${targetOptionValueId}"]`,
              );
            }
            if (!restoredButton && targetOptionValueId) {
              restoredButton = this.querySelector(
                `${VariantPickerDOM.colorSwatchButton}[data-option-value-id="${targetOptionValueId}"]`,
              );
            }

            if (restoredButton) {
              const allColorButtons = this.querySelectorAll(VariantPickerDOM.colorSwatchButton);
              allColorButtons.forEach(btn => {
                btn.classList.remove(VariantPickerDOM.colorSwatchSelectedClass);
                btn.setAttribute("aria-pressed", "false");
                btn.dataset.currentChecked = "false";
              });
              // Select the correct one
              restoredButton.classList.add(VariantPickerDOM.colorSwatchSelectedClass);
              restoredButton.setAttribute("aria-pressed", "true");
              restoredButton.dataset.currentChecked = "true";

              const colorGroup = restoredButton.closest("fieldset[data-color-group]");
              if (colorGroup) {
                const legend = colorGroup.querySelector("legend");
                if (legend) {
                  const selectedColorNameEl = colorGroup.querySelector(VariantPickerDOM.legendSelectedColorName);
                  if (selectedColorNameEl) {
                    const colorName = targetColorName || restoredButton.getAttribute("aria-label");
                    if (colorName) selectedColorNameEl.textContent = colorName;
                  }
                  const selectedColorInfo = legend.querySelector(VariantPickerDOM.legendSelectedColorInfo);
                  if (selectedColorInfo) {
                    selectedColorInfo.textContent = legend.dataset.productCode || restoredButton.dataset.colorHex || "";
                  }
                }
              }
            }

            const allLegends = this.querySelectorAll(VariantPickerDOM.legendColorGroupLabel);
            allLegends.forEach(legend => {
              const selectedColorInfo = legend.querySelector(VariantPickerDOM.legendSelectedColorInfo);
              if (selectedColorInfo) {
                const fieldset = legend.closest("fieldset[data-color-group]");
                const hasSelectedButton = fieldset?.querySelector(VariantPickerDOM.colorSwatchButtonSelected);
                if (!hasSelectedButton) selectedColorInfo.textContent = legend.dataset.productCode || "";
              }
            });
          }

          restoreNonColorOptions();

          // Use a small delay to ensure morph has fully completed before updating legends and syncing custom dropdown labels
          setTimeout(() => {
            restoreNonColorOptions();
            this.#initCustomDropdowns();
            this.#updateAtcButtonState();

            // Re-check and update legend after morph completes
            if (targetOptionValueId) {
              let restoredButton = null;
              if (targetUniqueColorId) {
                restoredButton = this.querySelector(
                  `${VariantPickerDOM.colorSwatchButton}[data-unique-color-id="${targetUniqueColorId}"]`,
                );
              }
              if (!restoredButton && targetLinkedProductId) {
                restoredButton = this.querySelector(
                  `${VariantPickerDOM.colorSwatchButton}[data-linked-product-id="${targetLinkedProductId}"][data-option-value-id="${targetOptionValueId}"]`,
                );
              }
              if (!restoredButton) {
                restoredButton = this.querySelector(
                  `${VariantPickerDOM.colorSwatchButton}[data-option-value-id="${targetOptionValueId}"]`,
                );
              }

              if (restoredButton && restoredButton.classList.contains(VariantPickerDOM.colorSwatchSelectedClass)) {
                const colorGroup = restoredButton.closest("fieldset[data-color-group]");
                if (colorGroup) {
                  const legend = colorGroup.querySelector("legend");
                  if (legend) {
                    const selectedColorNameEl = colorGroup.querySelector(VariantPickerDOM.legendSelectedColorName);
                    if (selectedColorNameEl) {
                      const colorName = targetColorName || restoredButton.getAttribute("aria-label");
                      if (colorName) selectedColorNameEl.textContent = colorName;
                    }
                    const selectedColorInfo = legend.querySelector(VariantPickerDOM.legendSelectedColorInfo);
                    if (selectedColorInfo) {
                      selectedColorInfo.textContent = legend.dataset.productCode || restoredButton.dataset.colorHex || "";
                    }
                  }
                }
              }
            }
          }, 50);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
              }),
            );
          }
        }
      })
      .catch(error => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = {id: newProductId, url: newProductUrl};
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: node => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter(m => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
    document.dispatchEvent(new CustomEvent("nova:main-morphed"));
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(VariantPickerDOM.selectedOptions);

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const {selectedOption} = this;
    if (!selectedOption) return undefined;
    const {optionValueId} = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll(VariantPickerDOM.selectedOptions));

    return selectedOptions.map(option => {
      const {optionValueId} = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
